import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { compact } from 'lodash-es';
import { Badge, BottomSheet, BottomSheetContentLayout, Box, Column, Grid, GridItem, MobileSummaryCard, Row, Typography, useDevice } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { AdditionalRecipients } from './AdditionalRecipients/AdditionalRecipients';
import { ClientRecipients } from './ClientRecipients/ClientRecipients';
import { useMigrateLegacyDraftRecipients } from './hooks/useMigrateLegacyDraftRecipients';
import { PrimaryContactPicker } from './PrimaryContactPicker/PrimaryContactPicker';
import { RecipientsOld } from './RecipientsOld';
import { hasNewDataShape } from './utils/hasNewDataShape';
export const { MobileRecipientsCardTID } = getTestIds();
const RecipientsTreeBody = (props)=>{
    const { contactDetailsProps, reviewers, onReviewersChange, amendmentInfoCallout, recipientsDisabled, showReviewerErrors } = props;
    const { existingContact, refreshExistingContacts } = contactDetailsProps;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PrimaryContactPicker, _object_spread({}, contactDetailsProps)),
            existingContact != null && /*#__PURE__*/ _jsx(GridItem, {
                span: 12,
                children: /*#__PURE__*/ _jsx(AdditionalRecipients, {
                    primaryContact: existingContact,
                    reviewers: reviewers,
                    disabled: recipientsDisabled,
                    amendmentInfoCallout: amendmentInfoCallout,
                    onChange: onReviewersChange,
                    refreshExistingContacts: refreshExistingContacts,
                    showReviewerErrors: showReviewerErrors
                })
            })
        ]
    });
};
const MobileRecipientsCard = (props)=>{
    const { contactDetailsProps, reviewers, recipientsDisabled } = props;
    const { existingContact, showCustomerDetailsWarning } = contactDetailsProps;
    const { t: recipTexts } = useLocaleKeys('agreementStudio.proposalMode');
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const selectedReviewersCount = reviewers.filter((r)=>r.clientContactId != null || r.email != null && r.email.length > 0).length;
    return /*#__PURE__*/ _jsxs(GridItem, {
        span: 12,
        children: [
            /*#__PURE__*/ _jsx(MobileSummaryCard, {
                items: [
                    {
                        title: recipTexts.recipientsTitle(),
                        actions: compact([
                            !recipientsDisabled && {
                                type: 'edit',
                                testId: MobileRecipientsCardTID.Card.Edit(),
                                bi: {
                                    name: 'edit_recipients'
                                },
                                onClick: ()=>setIsSheetOpen(true)
                            }
                        ]),
                        body: /*#__PURE__*/ _jsxs(Column, {
                            gap: 4,
                            children: [
                                /*#__PURE__*/ _jsx(Row, {
                                    alignItems: "center",
                                    gap: 4,
                                    children: existingContact != null ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                testId: MobileRecipientsCardTID.Card.Primary(),
                                                children: existingContact.fullName()
                                            }),
                                            selectedReviewersCount > 0 && /*#__PURE__*/ _jsx(Badge, {
                                                rounded: true,
                                                outlined: true,
                                                skin: "primary",
                                                testId: MobileRecipientsCardTID.Card.Badge(),
                                                children: recipTexts.primaryContact()
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx(Typography, {
                                        testId: MobileRecipientsCardTID.Card.Primary(),
                                        skin: showCustomerDetailsWarning ? 'error' : 'standard',
                                        children: recipTexts.selectContact()
                                    })
                                }),
                                selectedReviewersCount > 0 && /*#__PURE__*/ _jsx(Typography, {
                                    testId: MobileRecipientsCardTID.Card.Reviewers(),
                                    children: recipTexts.additionalRecipients({
                                        count: selectedReviewersCount
                                    })
                                })
                            ]
                        })
                    }
                ]
            }),
            /*#__PURE__*/ _jsx(BottomSheet, {
                open: isSheetOpen,
                onClose: ()=>setIsSheetOpen(false),
                children: /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                    header: {
                        title: recipTexts.recipientsTitle(),
                        paddingBottom: 16
                    },
                    contentGutter: "m",
                    children: /*#__PURE__*/ _jsx(Box, {
                        style: {
                            paddingTop: 12
                        },
                        children: /*#__PURE__*/ _jsx(Grid, {
                            align: "center",
                            rowGap: 8,
                            children: /*#__PURE__*/ _jsx(RecipientsTreeBody, _object_spread({}, props))
                        })
                    })
                })
            })
        ]
    });
};
const RecipientsTree = (props)=>{
    var _ref;
    const { contactDetailsProps, reviewers, onReviewersChange, amendmentInfoCallout, additionalRecipientsDisabled, showReviewerErrors, contactsLoaded, primaryContactId } = props;
    const { existingContact, refreshExistingContacts } = contactDetailsProps;
    const recipientsDisabled = (_ref = additionalRecipientsDisabled !== null && additionalRecipientsDisabled !== void 0 ? additionalRecipientsDisabled : contactDetailsProps.disabled) !== null && _ref !== void 0 ? _ref : false;
    const currentPrimaryId = existingContact === null || existingContact === void 0 ? void 0 : existingContact.id();
    const previousPrimaryId = useRef(currentPrimaryId);
    // eslint-disable-next-line no-restricted-syntax -- card↔bottom-sheet is a structural UX shift, not a primitive direction swap; matches RecipientsOld's existing pattern.
    const { isMobileDevice } = useDevice();
    useEffect(()=>{
        const previousId = previousPrimaryId.current;
        const swappedPrimary = previousId != null && currentPrimaryId != null && previousId !== currentPrimaryId;
        if (swappedPrimary && reviewers.length > 0) {
            onReviewersChange([]);
        }
        if (currentPrimaryId != null) {
            previousPrimaryId.current = currentPrimaryId;
        }
    }, [
        currentPrimaryId,
        reviewers.length,
        onReviewersChange
    ]);
    useMigrateLegacyDraftRecipients({
        primaryContact: existingContact,
        primaryContactId,
        reviewers,
        contactsLoaded,
        refreshExistingContacts,
        onReviewersChange
    });
    const bodyProps = {
        contactDetailsProps,
        reviewers,
        onReviewersChange,
        amendmentInfoCallout,
        recipientsDisabled,
        showReviewerErrors
    };
    return isMobileDevice ? /*#__PURE__*/ _jsx(MobileRecipientsCard, _object_spread({}, bodyProps)) : /*#__PURE__*/ _jsx(RecipientsTreeBody, _object_spread({}, bodyProps));
};
export const Recipients = (props)=>{
    var _ref;
    var _props_contactDetailsProps_existingContact;
    const { CRM_DRAFT_CONTACTS, FE_CRM } = useFeatureFlags();
    const { contacts, isLoading } = useContacts();
    const contactsLoaded = !isLoading;
    const draftAlreadyMigrated = useMemo(()=>hasNewDataShape({
            primary: props.contactDetailsProps.existingContact,
            reviewers: props.reviewers,
            contacts
        }), [
        props.contactDetailsProps.existingContact,
        props.reviewers,
        contacts
    ]);
    const [lockedToTree, setLockedToTree] = useState(()=>CRM_DRAFT_CONTACTS === true);
    useEffect(()=>{
        if (lockedToTree) return;
        if (!contactsLoaded) return;
        if (draftAlreadyMigrated) {
            setLockedToTree(true);
        }
    }, [
        lockedToTree,
        contactsLoaded,
        draftAlreadyMigrated
    ]);
    const clientContactId = (_ref = (_props_contactDetailsProps_existingContact = props.contactDetailsProps.existingContact) === null || _props_contactDetailsProps_existingContact === void 0 ? void 0 : _props_contactDetailsProps_existingContact.id()) !== null && _ref !== void 0 ? _ref : null;
    const clientEligible = props.customerId != null || clientContactId == null || props.isSampleRelationship;
    if (FE_CRM === true && clientEligible) {
        var _ref1, _props_additionalRecipientsDisabled;
        return /*#__PURE__*/ _jsx(ClientRecipients, {
            selectedClientId: props.selectedClientId,
            onSelectedClientIdChange: props.onSelectedClientIdChange,
            primaryContactId: props.primaryContactId,
            onPrimaryContactIdChange: props.onPrimaryContactIdChange,
            reviewers: props.reviewers,
            onReviewersChange: props.onReviewersChange,
            isSample: props.isSampleRelationship,
            onSampleChange: props.onSampleChange,
            onCreateClient: props.onCreateClient,
            onEditClient: props.onEditClient,
            onCreateContact: props.onCreateContact,
            showReviewerErrors: props.showReviewerErrors === true,
            showSignatoryError: props.contactDetailsProps.showCustomerDetailsWarning === true,
            amendmentInfoCallout: props.amendmentInfoCallout,
            disabled: (_ref1 = (_props_additionalRecipientsDisabled = props.additionalRecipientsDisabled) !== null && _props_additionalRecipientsDisabled !== void 0 ? _props_additionalRecipientsDisabled : props.contactDetailsProps.disabled) !== null && _ref1 !== void 0 ? _ref1 : false
        });
    }
    if (lockedToTree) {
        return /*#__PURE__*/ _jsx(RecipientsTree, _object_spread_props(_object_spread({}, props), {
            contactsLoaded: contactsLoaded
        }));
    }
    return /*#__PURE__*/ _jsx(RecipientsOld, _object_spread({}, props));
};
